/**
 * UsersModal — manage registered users.
 *
 * Lists every user from `listUsers` with their call sign, name, rank,
 * email, and current status. Each row has a small ✕ button that
 * deletes the user via `deleteUser`.
 */
import { useEffect, useState } from 'react'
import { workerGetV1, workerPostV1 } from '../../../api/worker'
import { modalOverlay, modalBox, entryRow } from './modalShared'
import ModalHeader from './ModalHeader'

interface User {
  callSign: string
  name: string
  rank: string
  email: string
  status: string
}

interface UsersModalProps {
  onClose: () => void
}

export default function UsersModal({ onClose }: UsersModalProps) {
  const [users, setUsers]     = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [msg, setMsg]         = useState('')

  useEffect(() => {
    workerGetV1<{ success: boolean; users?: User[]; error?: string }>('/api/v1/users/list')
      .then(res => {
        if (res.success && res.users) setUsers(res.users)
        else setMsg(res.error ?? 'Could not load users')
      })
      .catch(() => setMsg('Network error'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function remove(cs: string) {
    if (!confirm(`Remove ${cs}?`)) return
    try {
      const res = await workerPostV1<{ success: boolean; error?: string }>('/api/v1/users/delete', { callSign: cs })
      if (res.success) setUsers(u => u.filter(x => x.callSign !== cs))
      else alert(res.error ?? 'Failed')
    } catch { alert('Network error') }
  }

  return (
    <div
      style={modalOverlay}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={modalBox()}>
        <ModalHeader title={`👥 Manage Users (${users.length})`} onClose={onClose} />

        {loading ? (
          <div style={{ color: 'var(--text-hint)', textAlign: 'center', padding: 32 }}>Loading…</div>
        ) : msg ? (
          <div style={{ color: '#f87171', padding: 12 }}>{msg}</div>
        ) : (
          users.map((u, i) => (
            <div
              key={i}
              style={{ ...entryRow, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <div>
                <div style={{ fontWeight: 700, color: 'var(--accent)', fontSize: 13 }}>{u.callSign}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {u.name} · {u.rank} · {u.email}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <span
                  style={{
                    fontSize: 10,
                    padding: '2px 8px',
                    borderRadius: 4,
                    background: u.status === 'active' ? 'rgba(34,197,94,0.2)' : 'rgba(245,158,11,0.2)',
                    color: u.status === 'active' ? '#22c55e' : '#f59e0b',
                  }}
                >
                  {u.status}
                </span>
                <button
                  onClick={() => remove(u.callSign)}
                  style={{
                    background: 'rgba(239,68,68,0.15)',
                    border: '1px solid rgba(239,68,68,0.4)',
                    borderRadius: 6,
                    color: '#ef4444',
                    fontSize: 11,
                    padding: '3px 8px',
                    cursor: 'pointer',
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
